import { defineComponent, createLibrary } from '@customerio/hydra-copilot/react';
import type { PromptOptions } from '@customerio/hydra-copilot/react';
import { Card, CardHeader, CardTitle, CardSection, Grid, Stack, Text } from '@customerio/pluma-components/react';
import { useMemo } from 'react';
import { z } from 'zod';
import { Chart } from '@/components/highcharts';
import { AppErrorBoundary } from '@/components/app-error-boundary';

// Register Series as a named type so the prompt shows `Series[]`
// instead of the verbose inline `{name: string, data: number[]}[]`.
const SeriesSchema = z.object({
  name: z.string(),
  data: z.array(z.number()),
});
// Zod 4 throws if the same id is registered twice. This can happen on HMR or
// test re-evaluation because each re-execution creates a new SeriesSchema
// instance, so `get(SeriesSchema)` returns null even if 'Series' is already
// claimed by the previous instance. A try/catch is the safest guard.
try {
  z.globalRegistry.add(SeriesSchema, { id: 'Series' });
} catch {
  // 'Series' id already registered from a previous module evaluation — safe to ignore.
}

// ─── Chart ───────────────────────────────────────────────────────────────────

type ChartProps = {
  series: z.infer<typeof SeriesSchema>[];
  type?: 'line' | 'bar' | 'column' | 'pie' | 'area' | 'spline';
  categories?: string[];
};

function ChartRenderer({ series, type, categories }: ChartProps) {
  // Stable options reference prevents Highcharts re-initializing on every
  // streaming token when the props haven't actually changed.
  const options = useMemo(
    () => ({
      chart: { height: 300 },
      // For pie charts categories become slice labels; for cartesian they go on the x-axis
      ...(type !== 'pie' && categories != null ? { xAxis: { categories } } : {}),
      series: series.map((s) => ({
        type: type ?? 'line',
        name: s.name,
        data:
          type === 'pie' && categories != null
            ? categories.map((cat, i) => ({ name: cat, y: s.data[i] ?? 0 }))
            : s.data,
      })),
    }),
    [series, type, categories],
  );
  return <Chart options={options} />;
}

const ChartDef = defineComponent({
  name: 'Chart',
  description: 'Renders a Highcharts data visualization.',
  props: z.object({
    series: z.array(SeriesSchema),
    type: z.enum(['line', 'bar', 'column', 'pie', 'area', 'spline']).optional(),
    categories: z.array(z.string()).optional(),
  }),
  component: ({ props }) => (
    <AppErrorBoundary>
      <ChartRenderer series={props.series} type={props.type} categories={props.categories} />
    </AppErrorBoundary>
  ),
});

// ─── StatCard ─────────────────────────────────────────────────────────────────

const StatCardDef = defineComponent({
  name: 'StatCard',
  description: 'Displays a single metric label and value.',
  props: z.object({
    title: z.string(),
    value: z.string(),
  }),
  component: ({ props }) => (
    <AppErrorBoundary>
      <Card>
        <CardHeader>
          <CardTitle>{props.title}</CardTitle>
        </CardHeader>
        <CardSection>
          <Text size="xl" weight="semibold">
            {props.value}
          </Text>
        </CardSection>
      </Card>
    </AppErrorBoundary>
  ),
});

// ─── Card ─────────────────────────────────────────────────────────────────────

const CardDef = defineComponent({
  name: 'Card',
  description: 'A card container with an optional title. Use to wrap a Chart.',
  props: z.object({
    children: z.array(z.union([ChartDef.ref, StatCardDef.ref])),
    title: z.string().optional(),
  }),
  component: ({ props, renderNode }) => (
    <AppErrorBoundary>
      <Card>
        {props.title != null ? (
          <CardHeader>
            <CardTitle>{props.title}</CardTitle>
          </CardHeader>
        ) : null}
        <CardSection>{renderNode(props.children)}</CardSection>
      </Card>
    </AppErrorBoundary>
  ),
});

// ─── Grid ─────────────────────────────────────────────────────────────────────

const GridDef = defineComponent({
  name: 'Grid',
  description: 'Arranges cards in a multi-column layout.',
  props: z.object({
    children: z.array(z.union([StatCardDef.ref, CardDef.ref])),
    columns: z.number().optional(),
    gap: z.enum(['100', '200', '300', '400']).optional(),
  }),
  component: ({ props, renderNode }) => (
    <AppErrorBoundary>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <Grid columns={props.columns ?? 2} gap={(props.gap ?? '200') as any}>
        {renderNode(props.children)}
      </Grid>
    </AppErrorBoundary>
  ),
});

// ─── Root ─────────────────────────────────────────────────────────────────────

const RootDef = defineComponent({
  name: 'Root',
  description: 'Top-level container. Every response must assign root = Root(...).',
  props: z.object({
    children: z.array(z.union([StatCardDef.ref, CardDef.ref, GridDef.ref])),
  }),
  component: ({ props, renderNode }) => (
    <AppErrorBoundary>
      <Stack gap="200">{renderNode(props.children)}</Stack>
    </AppErrorBoundary>
  ),
});

// ─── Library ──────────────────────────────────────────────────────────────────

export const copilotLibrary = createLibrary({
  root: 'Root',
  components: [ChartDef, StatCardDef, CardDef, GridDef, RootDef],
  componentGroups: [
    {
      name: 'Layout',
      components: ['Root', 'Grid'],
      notes: [
        '- Always write root = Root([...]) as the very first statement.',
        '- Grid defaults to 2 columns. Use columns=3 for stat overviews.',
      ],
    },
    {
      name: 'Cards',
      components: ['StatCard', 'Card'],
      notes: [
        '- StatCard(title, value): for a single metric; value is a pre-formatted string e.g. "1,234" or "$42K".',
        '- Card([chart], title?): use to wrap a Chart with an optional title.',
      ],
    },
    {
      name: 'Charts',
      components: ['Chart'],
      notes: [
        '- Use "line" or "area" for trends over time, "column" or "bar" for comparisons, "pie" for proportions.',
        '- data is always number[]; categories provides the labels (x-axis for cartesian, slice names for pie).',
        '- categories length must match data length in every series.',
        '- Pie: pass exactly one series; categories become the slice labels.',
        '- Chart has no title — wrap it in Card to add a title.',
      ],
    },
  ],
});

export const copilotPromptOptions: Omit<PromptOptions, 'preamble'> = {
  examples: [
    // Stat grid
    `root = Root([grid])
grid = Grid([c1, c2, c3], 3)
c1 = StatCard("Total Users", "12,847")
c2 = StatCard("Active", "9,203")
c3 = StatCard("Churn Rate", "2.4%")`,

    // Single chart in a card
    `root = Root([card])
card = Card([chart], "Monthly Signups")
chart = Chart([{name:"Signups",data:[120,145,132,178]}], "line", ["Jan","Feb","Mar","Apr"])`,

    // Chart + stat grid
    `root = Root([grid, card])
grid = Grid([c1, c2], 2)
c1 = StatCard("Total Revenue", "$148K")
c2 = StatCard("Growth", "+18%")
card = Card([chart], "Revenue Trend")
chart = Chart([{name:"Revenue",data:[42,51,48,63,55,70]}], "area", ["Jan","Feb","Mar","Apr","May","Jun"])`,

    // Pie chart — one series, categories become slice labels
    `root = Root([card])
card = Card([chart], "Messages by Channel")
chart = Chart([{name:"Channels",data:[38,22,18,14,8]}], "pie", ["Email","Push","SMS","In-App","Webhook"])`,
  ],
};
